import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

const Container = styled(Box)(({ theme }) => ({
  padding: '60px 40px',
  maxWidth: '900px',
  margin: '0 auto',
  [theme.breakpoints.down('md')]: {
    padding: '40px 30px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '30px 20px',
  },
}));

const Title = styled(Typography)(({ theme }) => ({
  fontSize: '42px',
  fontWeight: 'bold',
  color: '#FFFFFF',
  fontFamily: '"Press Start 2P", cursive',
  marginBottom: '40px',
  textShadow: '0 0 20px rgba(0, 240, 255, 0.5)',
  [theme.breakpoints.down('lg')]: {
    fontSize: '32px',
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '24px',
    marginBottom: '30px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '18px',
    marginBottom: '20px',
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '18px',
  fontWeight: 'bold',
  color: '#00F0FF',
  fontFamily: '"Press Start 2P", cursive',
  marginBottom: '15px',
  marginTop: '30px',
  [theme.breakpoints.down('md')]: {
    fontSize: '14px',
    marginTop: '20px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '12px',
    marginTop: '15px',
  },
}));

const Content = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  color: '#6B6A78',
  fontFamily: '"JetBrains Mono", monospace',
  lineHeight: 1.8,
  marginBottom: '15px',
  [theme.breakpoints.down('md')]: {
    fontSize: '12px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '11px',
  },
}));

const TermsOfService: React.FC = () => {
  return (
    <Container>
      <Title>TERMS OF SERVICE</Title>
      
      <Content>Last updated: June 18, 2024</Content>

      <SectionTitle>1. ACCEPTANCE OF TERMS</SectionTitle>
      <Content>
        By accessing and using StegSafe, you agree to be bound by these Terms of Service. 
        If you do not agree to these terms, please do not use this service.
      </Content>

      <SectionTitle>2. DESCRIPTION OF SERVICE</SectionTitle>
      <Content>
        StegSafe is a free online image steganography tool that allows users to hide and 
        extract secret messages inside images using advanced pixel-based data encoding. 
        The service is provided "as is" without any warranties.
      </Content>

      <SectionTitle>3. USER RESPONSIBILITIES</SectionTitle>
      <Content>
        You are responsible for maintaining the confidentiality of your passwords and for 
        all activities that occur under your account. You agree to use the service only for 
        lawful purposes and in accordance with these Terms.
      </Content>

      <SectionTitle>4. PROHIBITED USES</SectionTitle>
      <Content>
        You may not use StegSafe for any illegal purposes, including but not limited to:
        <br />• Storing or transmitting illegal content
        <br />• Violating intellectual property rights
        <br />• Engaging in fraudulent activities
        <br />• Compromising the security of the service
      </Content>

      <SectionTitle>5. PRIVACY AND DATA SECURITY</SectionTitle>
      <Content>
        StegSafe processes all images locally in your browser. Your images and messages are 
        never uploaded to any server. We do not store, collect, or have access to your data. 
        All processing happens entirely on your device.
      </Content>

      <SectionTitle>6. DISCLAIMER OF WARRANTIES</SectionTitle>
      <Content>
        StegSafe is provided "as is" without any warranties, express or implied. We do not 
        guarantee that the service will be uninterrupted, secure, or error-free.
      </Content>

      <SectionTitle>7. LIMITATION OF LIABILITY</SectionTitle>
      <Content>
        In no event shall StegSafe or its creator be liable for any indirect, incidental, 
        special, consequential, or punitive damages arising from your use of the service.
      </Content>

      <SectionTitle>8. INTELLECTUAL PROPERTY</SectionTitle>
      <Content>
        All content, features, and functionality of StegSafe are owned by Rishi Patel and 
        are protected by international copyright, trademark, and other intellectual property laws.
      </Content>

      <SectionTitle>9. MODIFICATIONS TO TERMS</SectionTitle>
      <Content>
        We reserve the right to modify these terms at any time. Continued use of the service 
        after such modifications constitutes acceptance of the updated terms.
      </Content>

      <SectionTitle>10. CONTACT INFORMATION</SectionTitle>
      <Content>
        For questions about these Terms of Service, please contact us through the appropriate 
        channels provided on the website.
      </Content>

      <Box sx={{ mt: 6, p: 3, border: '1px solid #39FF14', backgroundColor: 'rgba(57, 255, 20, 0.05)', borderRadius: '5px' }}>
        <Typography sx={{ color: '#39FF14', fontFamily: '"Press Start 2P", cursive', fontSize: '12px', mb: 2 }}>
          IMPORTANT NOTE
        </Typography>
        <Typography sx={{ color: '#6B6A78', fontFamily: '"JetBrains Mono", monospace', fontSize: '14px' }}>
          StegSafe is a client-side only application. All data processing happens locally on your device. 
          We have no access to your images, messages, or passwords. Use this tool responsibly and ethically.
        </Typography>
      </Box>
    </Container>
  );
};

export default TermsOfService;
